import { Alert, Form, Radio, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { cadastroCotaUsuarioApi } from "../cadastroCotaUsuario.api";
import { FluxoApartamento, FluxoResposta } from "../cadastroCotaUsuario.types";
import { ApartamentoFields } from "../components/ApartamentoFields";
import {
  filterSelectOption,
  OnboardingFrame,
  SubmitButton,
} from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

export function EtapaApartamento({ fluxo, submitting, onConcluir }: Props) {
  const [form] = Form.useForm();
  const modo = Form.useWatch("modo", form) ?? "EXISTENTE";
  const dadosApartamento =
    (fluxo.dadosPreenchidos?.apartamento as Record<string, unknown> | undefined) ?? {};
  const aptoId = (dadosApartamento.aptoId as number | undefined) ?? fluxo.contextoFluxo.aptoId;
  const empreendimentoId = fluxo.contextoFluxo.empreendimentoId;
  const apartamentosRecebidos = fluxo.etapaAtual === "AGUARDANDO_APARTAMENTO"
    ? fluxo.detalhesEtapa?.apartamentos : undefined;
  const [consulta, setConsulta] = useState<{
    empreendimentoId: number;
    apartamentos: FluxoApartamento[];
    erro: boolean;
  } | null>(null);

  useEffect(() => {
    if (apartamentosRecebidos !== undefined || !empreendimentoId) return;
    let active = true;
    setConsulta(null);
    cadastroCotaUsuarioApi.buscarApartamentos(empreendimentoId).then(
      (apartamentos) => {
        if (active) setConsulta({ empreendimentoId, apartamentos, erro: false });
      },
      () => {
        if (active) setConsulta({ empreendimentoId, apartamentos: [], erro: true });
      }
    );
    return () => { active = false; };
  }, [apartamentosRecebidos, empreendimentoId]);

  const resultado = consulta?.empreendimentoId === empreendimentoId ? consulta : null;
  const carregando = apartamentosRecebidos === undefined && Boolean(empreendimentoId) && !resultado;
  const erro = apartamentosRecebidos === undefined && resultado?.erro;
  const apartamentosBase = apartamentosRecebidos ?? resultado?.apartamentos ?? [];
  const apartamentos = aptoId && !apartamentosBase.some((item) => item.id === aptoId)
    ? [...apartamentosBase, { id: aptoId, nome: dadosApartamento.nome as string | null }]
    : apartamentosBase;

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Qual apartamento corresponde à sua cota?"
      description="Selecione um apartamento do empreendimento ou cadastre um novo."
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          modo: dadosApartamento.modo ?? "EXISTENTE",
          aptoId,
          apartamento: {
            nome: dadosApartamento.nome,
            capacidade: dadosApartamento.capacidade,
            quartos: dadosApartamento.quartos,
            banheiros: dadosApartamento.banheiros,
            metragem: dadosApartamento.metragem,
            varanda: dadosApartamento.varanda,
            terreo: dadosApartamento.terreo,
            estacionamento: dadosApartamento.estacionamento,
          },
        }}
        onFinish={(values) => {
          if (values.modo === "EXISTENTE") {
            onConcluir({
              modo: "EXISTENTE",
              aptoId: values.aptoId,
            });
            return;
          }

          onConcluir({
            modo: "NOVO",
            apartamento: values.apartamento ?? {},
          });
        }}
      >
        <Form.Item
          label="Como você quer informar o apartamento?"
          name="modo"
          rules={[{ required: true, message: "Selecione uma opção." }]}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="EXISTENTE">Selecionar apartamento existente</Radio>
              <Radio value="NOVO">Cadastrar novo apartamento</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {modo === "EXISTENTE" ? (
          <>
            {erro ? (
              <Alert
                type="error"
                showIcon
                message="Não foi possível carregar os apartamentos. Tente novamente ao reabrir esta etapa."
                style={{ marginBottom: 16 }}
              />
            ) : !carregando && !apartamentos.length ? (
              <Alert
                type="info"
                showIcon
                message="Nenhum apartamento disponível para seleção neste empreendimento. Você pode cadastrar um novo."
                style={{ marginBottom: 16 }}
              />
            ) : null}

            <Form.Item
              label="Apartamento"
              name="aptoId"
              rules={[{ required: true, message: "Selecione o apartamento." }]}
            >
              <Select
                showSearch
                loading={carregando}
                disabled={carregando}
                optionFilterProp="label"
                filterOption={filterSelectOption}
                options={apartamentos.map((apartamento) => ({
                  label: apartamento.nome ?? `Apartamento ${apartamento.id}`,
                  value: apartamento.id,
                }))}
                placeholder="Escolha o apartamento"
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Typography.Paragraph type="secondary">
              Se o apartamento ainda não existir na lista, preencha os dados abaixo para cadastrá-lo.
            </Typography.Paragraph>
            <ApartamentoFields />
          </>
        )}

        <SubmitButton label="Continuar" loading={submitting} />
      </Form>
    </OnboardingFrame>
  );
}
